/* -------------------------------------------------------------------------- */
/*                                  Packages                                  */
/* -------------------------------------------------------------------------- */
import { navigate } from "gatsby"
import * as React from "react"
import { Logo } from "abmg-components"
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image"
import { twMerge } from "tw-merge"
import { ChevronLeft, ChevronRight, X } from "react-feather"
import { AnimatePresence, motion } from "framer-motion"
import { InlineWidget } from "react-calendly"
import { useWindowWidth } from "@react-hook/window-size"

/* -------------------------------------------------------------------------- */
/*                             External Components                            */
/* -------------------------------------------------------------------------- */
import ProgressBar from "@components/global/funnels/ProgressBar"
import Modal from "@components/global/Modal"
import SavePopUp from "@components/global/funnels/SavePopup"
import UnderlineField from "@components/global/UnderlineField"

/* -------------------------------------------------------------------------- */
/*                               External Hooks                               */
/* -------------------------------------------------------------------------- */
import useClientSideOnly from "@hooks/use-client-side-only"

/* -------------------------------------------------------------------------- */
/*                               External Utils                               */
/* -------------------------------------------------------------------------- */
import { sendGaCalendlyData } from "@utils/gtm"
import { convertPhoneNumberToE164, navigateWithUrlParams } from "@utils/utils"

/* -------------------------------------------------------------------------- */
/*                                 Types                                      */
/* -------------------------------------------------------------------------- */
type GatsbyImage = {
  alt?: string
  gatsbyImageData: IGatsbyImageData
}
type Form = {
  heading: string
  fields: Array<Field>
  type: "contact" | "radio" | "range" | "select"
}
export type Field = {
  fields: Array<Field>
  errorMessage?: string
  heading?: string
  id?: string
  inputType?: "checkbox" | "radio" | "text"
  labelText?: ""
  name: string
  value?: string
}
type Props = {
  bankerData: {
    calendly?: string
    slug: string
  }
  breakpoints: {
    desktop: string
    minTablet: string
    mobile: string
  }
  classes?: {
    closeLink?: string
    formSide?: string
    formSideLogoContainer?: string
    image?: string
    imageSide?: string
  }
  currentStep: number
  exitButton?: {
    icon?: React.ReactElement
    link?: string
  }
  formData: object
  formSide: {
    form: Form
    logo: React.ReactElement | GatsbyImage
  }
  imageSide: {
    description: {
      text: React.ReactElement | "string"
    }
    image: GatsbyImage
  }
  save: Function
  totalSteps: number
  updateFunnelData: Function
}

/* -------------------------------------------------------------------------- */
/*                              Default Component                             */
/* -------------------------------------------------------------------------- */
export default function FunnelStepScreen(props: Props) {
  const isMounted = useClientSideOnly()
  const [isModalOpen, setIsModalOpen] = React.useState(false)

  React.useEffect(() => {
    if (props.currentStep === "confirmation")
      navigateWithUrlParams({
        path: `confirmation`,
        location: window.location,
        navigateState: {
          currentStep: "confirmation",
          formData: props.formData,
          startPath: props.startPath,
        },
      })
  }, [props.currentStep, props.formData, props.startPath])

  const [calendlyEventScheduled, setCalendlyEventScheduled] =
    React.useState(false)

  React.useEffect(() => {
    const handleMessage = e => {
      // Calendly Events
      const event = e.data.event
      if (event && event.startsWith("calendly")) {
        sendGaCalendlyData({ data: e.data, window })
        // if (event === "calendly.page_height") {
        //   const height = e.data.payload.height
        // }
        if (event === "calendly.event_scheduled") {
          console.log("scheduled")
          setCalendlyEventScheduled(true)
        }
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [])

  const navigateStep = step => {
    screenNavigationStarted.current = true
    props.reactHookFormProps.handleSubmit(formData => {
      window.scrollTo({
        top: 0,
        behavior: "instant",
      })
      try {
        if (step < props.totalSteps) {
          if (step > props.currentStep) {
            setSlideDirection("left")
          } else if (step < props.currentStep) {
            setSlideDirection("right")
          }

          props.updateFunnelData({
            formData,
            currentStep: step,
            passedSteps: {
              ...props.passedSteps,
              [`step${props.currentStep}`]: true,
            },
          })
          // setTimeout(() => {
          //   containerRef.current.scrollIntoView({ behavior: "smooth" })
          // }, 500)
        } else {
          if (props.exitFunnelFunction) {
            props.exitFunnelFunction({
              updatedFormData: formData,
              calendlyEventScheduled,
            })
          }
          navigateWithUrlParams({
            path: props.endPath || `confirmation`,
            location: window.location,
            navigateState: {
              currentStep: `confirmation`,
              formData,
              startPath: props.startPath,
            },
          })
        }
      } catch (err) {
        console.error(err)
      }
    })()
  }

  const [slideDirection, setSlideDirection] = React.useState("left")

  const [showImageSide, setShowImageSide] = React.useState(true)

  const MotionChrevronLeft = motion(ChevronLeft)
  const MotionChevronRight = motion(ChevronRight)
  const [showingSteps, setShowingSteps] = React.useState(
    props.breakpoints?.minTablet && isMounted ? [1, 2, 3, 4, 5] : [1, 2, 3],
  )
  React.useEffect(() => {
    if (isNaN(props.currentStep)) return
    let counter = props.currentStep + 1
    let stepsArray = []
    if (counter === props.totalSteps) {
      for (
        let i = counter - (props.breakpoints?.minTablet ? 4 : 2);
        i <= counter;
        i++
      ) {
        if (i > 0) stepsArray.push(i)
      }
    } else {
      while (counter % (props.breakpoints?.minTablet ? 5 : 3) !== 0) {
        counter++
      }

      counter = props.totalSteps < counter ? props.totalSteps : counter
      for (
        let i = counter - (props.breakpoints?.minTablet ? 4 : 2);
        i <= counter;
        i++
      ) {
        if (i > 0) stepsArray.push(i)
      }
    }

    setShowingSteps(stepsArray)
  }, [props.currentStep, props.breakpoints?.minTablet, props.totalSteps])

  const screenNavigationStarted = React.useRef(false)

  const custom = {
    isMounted,
    slideDirection,
    screenNavigationStarted: screenNavigationStarted.current,
  }

  const screenVariants = {
    initial: custom => ({
      x:
        !custom.isMounted || !custom.screenNavigationStarted
          ? 0
          : custom.slideDirection === "left"
            ? `100%`
            : `-100%`,
    }),
    animate: custom => ({
      x: 0,
    }),
    exit: custom => ({
      x: custom.slideDirection === "left" ? `-100%` : `100%`,
    }),
  }
  const transitionDuration = 0.5

  const containerRef = React.useRef(null)

  return (
    <div ref={containerRef}>
      {props.currentStep !== "confirmation" && (
        <AnimatePresence custom={custom} mode="popLayout">
          <motion.div
            // layout
            key={`screen-${props.currentStep}`}
            className={twMerge(`grid grid-cols-1 min-h-[100vh]`)}
            initial="initial"
            animate="animate"
            exit="exit"
            custom={custom}
            variants={screenVariants}
            transition={{
              bounceDamping: 100,
              duration: 0.5,
            }}
            style={{
              opacity: isMounted ? 1 : 0,
            }}
          >
            {/* Image Side */}
            {props.imageSide && (
              <motion.div
                // layout
                // layoutId="side"
                className={twMerge(
                  `bg-arctic-blue col-start-1 col-end-2 flex flex-col gap-[20px] justify-center justify-self-start items-center [padding:69px_42px] relative row-start-1 row-end-1 z-[15] [@media(max-width:1451px)]:w-[40%] [@media(min-width:1452px)]:w-[50%]  ${
                    props.classes?.imageSide ? props.classes.imageSide : ""
                  } `,
                )}
                style={{
                  "--imageSideWidth": !showImageSide
                    ? 0
                    : props.breakpoints?.desktop
                      ? "50%"
                      : props.breakpoints?.minTablet
                        ? "40%"
                        : 0,
                }}
                initial={{
                  x: !showImageSide || props.breakpoints?.mobile ? "-100%" : 0,
                  // width: props.breakpoints?.desktop ? "50%" : "40%",
                  width: Object.values(props.breakpoints).every(
                    breakpoint => !breakpoint,
                  )
                    ? null
                    : props.breakpoints?.desktop
                      ? "50%"
                      : "40%",
                  opacity: props.breakpoints?.minTablet ? 1 : 0,
                }}
                animate={{
                  x: !showImageSide || props.breakpoints?.mobile ? "-100%" : 0,
                  // width: Object.values(props.breakpoints).every(
                  //   breakpoint => !breakpoint,
                  // )
                  //   ? null
                  //   : props.breakpoints?.desktop
                  //     ? "50%"
                  //     : "40%",
                  opacity: props.breakpoints?.minTablet ? 1 : 0,
                }}
                transition={{
                  duration: transitionDuration,
                }}
              >
                {props.imageSide.logo || (
                  <Logo logo="abmg-footer-logo-with-icon-white" />
                )}
                {props.imageSide.image && (
                  <GatsbyImage
                    alt={props.imageSide.image.alt || ""}
                    image={props.imageSide.image.gatsbyImageData}
                    className={twMerge(
                      `w-fit max-w-[465px] z-[30] ${
                        props.imageSide.image.classes?.default || ""
                      }`,
                    )}
                  />
                )}
                <div
                  className={twMerge(
                    `font-variant-numeric--primary max-w-[400px] text-white z-[10] ${
                      props.imageSide.description.classes?.default || ""
                    }`,
                  )}
                >
                  {props.imageSide.description.text}
                </div>
                {/* Chevron Slider */}
                <motion.button
                  className="[--size:114px] absolute bg-arctic-blue grid  items-center h-[var(--size)] -right-[calc(var(--size)/2)] rounded-[228px] top-[calc(50%-var(--size)/2))] -translate-y-[50%] w-[var(--size)] z-[20]"
                  onClick={() =>
                    setShowImageSide(prevShowImageSide => !prevShowImageSide)
                  }
                  initial={{
                    x: props.breakpoints?.mobile ? "-100%" : 0,
                  }}
                  animate={{
                    x: props.breakpoints?.mobile ? "-100%" : 0,
                  }}
                  transition={{
                    delay:
                      props.breakpoints?.mobile && showImageSide
                        ? transitionDuration
                        : 0,
                    duration: 0,
                    ease: "easeIn",
                  }}
                >
                  <MotionChrevronLeft
                    className="ml-[59px] nr-[13px]"
                    color={"#fff"}
                    height={42}
                    width={42}
                    strokeWidth={2}
                    animate={{
                      x: [null, -3, 3, 0],
                      rotateY: !showImageSide ? -180 : 0,
                    }}
                    transition={{
                      rotateY: {
                        duration: 0.5,
                        ease: "easeInOut",
                      },
                      x: {
                        duration: 0.8,
                        ease: "linear",
                        repeat: Infinity,
                      },
                    }}
                  />
                </motion.button>
              </motion.div>
            )}
            {/* Form Side */}
            <motion.div
              // layout
              // layoutId="side"
              className={twMerge(
                `bg-grey-1 col-start-1 col-end-2 flex flex-col gap-[43px] items-center justify-self-end justify-center [padding-block:clamp(28px,3vw,72px)] relative row-start-1 row-end-1 [@media(max-width:1079px)]:w-full [@media(min-width:1080px)_and_(max-width:1451px)]:w-[60%] [@media(min-width:1452px)]:w-[50%]  ${
                  props.classes?.formSide ? props.classes?.formSide : ""
                }`,
              )}
              // initial={{
              //   width:
              //     !showImageSide || props.breakpoints?.mobile
              //       ? `100%`
              //       : props.breakpoints?.desktop
              //         ? "50%"
              //         : "60%",
              // }}
              animate={{
                width: Object.values(props.breakpoints).every(
                  breakpoint => !breakpoint,
                )
                  ? null
                  : !showImageSide || props.breakpoints?.mobile
                    ? `100%`
                    : props.breakpoints?.desktop
                      ? "50%"
                      : "60%",
              }}
              transition={{
                duration: transitionDuration,
              }}
            >
              {/* Close Link */}
              <button
                className={twMerge(
                  `absolute right-[18px] top-[16px] ${
                    props.classes?.closeLink ? props.classes?.closeLink : ""
                  }`,
                )}
                onClick={() => {
                  navigate(
                    `${
                      props.exitButton?.link || props.bankerData?.slug
                        ? `/${props.bankerData.slug}/`
                        : "/"
                    }${window.location.search ? window.location.search : ""}`,
                  )
                }}
              >
                {props.exitButton?.icon || (
                  <X className="stroke-arctic-blue" strokeWidth={2} />
                )}
              </button>

              {/* Logo */}
              <div
                className={twMerge(
                  ` ${props.breakpoints?.minTablet ? `hidden` : ""} ${
                    props.classes?.formSideLogoContainer
                      ? props.classes?.formSideLogoContainer
                      : ""
                  }`,
                )}
              >
                {props.formSide.logo || <Logo logoClass={`w-[99px]`} />}
              </div>
              {/* Steps Progress Bar & Heading */}
              <div className="flex flex-col items-center max-w-[512px] w-full">
                {/* Steps Progress Bar */}
                <div
                  className={twMerge(
                    `flex ${
                      showingSteps.length === 1
                        ? "justify-center"
                        : "justify-between"
                    } mb-[20px] relative ${
                      props.breakpoints?.minTablet ? `w-[476px]` : `w-[248px]`
                    }`,
                  )}
                >
                  {props.currentStep - 1 > -1 && (
                    <MotionChrevronLeft
                      className={twMerge(
                        `[--width:15px] absolute h-[var(--width)] -left-[var(--width)] stroke-pacific-blue-4 top-[50%] -translate-y-[50%] w-[var(--width)]`,
                      )}
                      initial={{
                        translateY: "-50%",
                      }}
                      animate={{
                        x: [null, -1, 1, 0],
                        transition: {
                          duration: 0.8,
                          ease: "linear",
                          repeat: Infinity,
                        },
                      }}
                      {...props.gaProps?.navigateFunnel}
                      onClick={() => navigateStep(props.currentStep - 1)}
                    />
                  )}
                  {/* Steps Line */}
                  <div
                    className={twMerge(
                      `absolute bg-pacific-blue-4 h-[1px] top-[50%] -translate-y-[50%] w-full`,
                    )}
                  ></div>
                  {showingSteps.map((value, i) => {
                    const step = value - 1
                    const stepIsClickable = props.passedSteps[`step${step}`]
                    return (
                      <button
                        key={`step-${i}`}
                        className={twMerge(
                          `${
                            step === props.currentStep
                              ? `bg-atlantic-blue text-grey-1`
                              : stepIsClickable
                                ? `bg-pacific-blue-4 text-grey-1`
                                : `bg-white border border-pacific-blue-4 text-pacific-blue-4`
                          } font-variant-numeric--primary flex justify-center items-center font-bold h-[20px] leading-[10px] rounded-full text-[9px] text-center tracking-[.27px] w-[20px] z-[5]`,
                        )}
                        {...props.gaProps?.navigateFunnel}
                        onClick={() => {
                          if (stepIsClickable) {
                            navigateStep(step)
                          }
                        }}
                      >
                        {value}
                      </button>
                    )
                  })}

                  {props.currentStep + 1 < props.totalSteps && (
                    <MotionChevronRight
                      className={twMerge(
                        `[--width:15px] absolute h-[var(--width)] -right-[var(--width)] stroke-pacific-blue-4 top-[50%] -translate-y-[50%] w-[var(--width)]`,
                      )}
                      initial={{
                        translateY: "-50%",
                      }}
                      animate={{
                        x: [null, 1, -1, 0],
                        transition: {
                          duration: 0.8,
                          ease: "linear",
                          repeat: Infinity,
                        },
                      }}
                      {...props.gaProps?.navigateFunnel}
                      onClick={() => navigateStep(props.currentStep + 1)}
                    />
                  )}
                </div>
                <h3
                  className={twMerge(
                    `font-variant-numeric--primary font-[500] sans leading-[44px] text-[36px] text-balance text-baltic-blue text-center -tracking-[.72px] ${
                      props.breakpoints?.mobile ? "w-[309px]" : ""
                    } ${
                      props.formSide?.heading?.classes
                        ? props.formSide.heading.classes
                        : ""
                    }`,
                  )}
                >
                  {props.formSide.heading.text}
                </h3>
              </div>

              {/* Form Content */}
              <div
                className={`flex flex-col min-h-[413px] justify-center max-w-[454px] w-full ${
                  props.breakpoints?.minTablet ? "" : "[padding-inline:10px]"
                }`}
              >
                {props.stepName === "booking" ? (
                  <InlineWidget
                    url={props.bookingUrl}
                    prefill={{
                      email: props.formData["email"],
                      name:
                        props.formData["first_name"] +
                        " " +
                        props.formData["last_name"],
                      location: convertPhoneNumberToE164({
                        phoneNumber: props.formData["phone_cell"] || "",
                      }),
                    }}
                    styles={{
                      height: "454px",
                    }}
                    className={twMerge(
                      `!p-[1px] ${props.formSide.form.classes?.default || ""}`,
                    )}
                  />
                ) : (
                  <form
                    className={twMerge(
                      `${props.formSide.form.classes?.default || ""}`,
                    )}
                  >
                    <div
                      className={twMerge(
                        `${
                          props.formSide.fieldsContainer?.classes?.default || ""
                        }`,
                      )}
                    >
                      {props.formSide.fields.map((field, i) => {
                        const groupedFieldNames = props.formSide.fields.map(
                          field =>
                            field.formElement?.attributes?.name ||
                            field.inputGroup?.name,
                        )
                        return (
                          <UnderlineField
                            key={`field-${i}`}
                            form={props.reactHookFormProps}
                            field={field}
                            groupedFieldNames={groupedFieldNames}
                            useTabToNextField
                          />
                        )
                      })}
                    </div>
                  </form>
                )}
              </div>
              {/* Buttons/Progress Bar Container */}
              <div className="flex flex-col gap-[38px] max-w-[328px] w-full ">
                {/* Buttons Container */}
                <div
                  className={twMerge(`flex flex-col gap-[15px] [&_*]:w-full`)}
                >
                  <button
                    className="button button--tertiary"
                    onClick={() => {
                      if (props.updateFunnelData) {
                        props.reactHookFormProps.handleSubmit(formData => {
                          props.updateFunnelData(
                            {
                              formData,
                              currentStep: props.currentStep,
                              passedSteps: {
                                ...props.passedSteps,
                                [`step${props.currentStep}`]: true,
                              },
                            },
                            true,
                          )
                          setIsModalOpen(true)
                        })()
                      }
                    }}
                  >
                    Save and Finish Later
                  </button>
                  <button
                    className="button button--primary"
                    {...props.gaProps?.navigateFunnel}
                    onClick={() => {
                      navigateStep(props.currentStep + 1)
                    }}
                    disabled={
                      props.shouldDisableNextButton !== undefined
                        ? props.shouldDisableNextButton
                          ? true
                          : false
                        : props.stepName === "booking"
                          ? !calendlyEventScheduled
                            ? true
                            : false
                          : false
                    }
                  >
                    Next
                  </button>
                </div>
                <ProgressBar
                  totalSteps={props.totalSteps - 1}
                  currentStep={props.currentStep}
                />
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
      <Modal isModalOpen={isModalOpen} closeModal={() => setIsModalOpen(false)}>
        <SavePopUp
          deleteSavedData={props.deleteSavedData}
          closePopup={() => setIsModalOpen(false)}
          startPath={props.startPath}
        />
      </Modal>
    </div>
  )
}
